import React from 'react';

import { Card, Flex, Image, Progress, Space, Tooltip } from 'antd-v5';
import { startCase } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { StatusTag, Text } from 'scouting-ui/lib';
import { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';
import type { ColorType } from 'scouting-ui/lib/types';
import variables from 'scouting-ui/lib/variables';

import type {
  Award,
  MeritBadge,
  MeritBadgeV1,
  RankV1,
  YouthAwardAdvancement,
  YouthMeritBadgeAdvancementRecord,
  YouthRankAdvancement,
} from '@appTypes/esb';

import styles from './AdvancementCard.module.scss';
import { advancementStatusToTagStatus } from './utils';

type AdvancementCardProps =
  | {
      type: 'rank';
      rank: RankV1;
      youthRecord?: YouthRankAdvancement;
    }
  | {
      type: 'meritBadge';
      meritBadge: MeritBadgeV1 | MeritBadge;
      youthRecord?: YouthMeritBadgeAdvancementRecord;
    }
  | {
      type: 'award';
      award: Award;
      youthRecord?: YouthAwardAdvancement;
    };

function BaseCard(props: {
  image: string;
  imageAlt: string;
  tooltip?: React.ReactNode;
  name: string;
  showProgress?: boolean;
  progress: number;
  label?: React.ReactNode;
  status?: string;
}) {
  const {
    image,
    imageAlt,
    name,
    showProgress = false,
    tooltip,
    progress,
    label,
    status = '',
  } = props;

  const barColors = {
    [StatusProp.APPROVED]: 'color-info',
    [StatusProp.AWARDED]: 'color-yellow',
    [StatusProp.COMPLETED]: 'color-success',
    [StatusProp.DECLINED]: 'color-red',
    [StatusProp.INPROGRESS]: 'color-orange',
    [StatusProp.NOT_STARTED]: 'color-primary-scoutingWarmGray',
    [StatusProp.PENDING]: 'color-primary-scoutingWarmGray',
    [StatusProp.STARTED]: 'color-orange',
    default: 'color-primary-scoutingWarmGray',
  };
  const colorProp = (barColors[status as keyof typeof barColors] ||
    barColors.default) as ColorType;

  const barColor = variables[colorProp];

  const barProps = {
    ['--ant-5-sbl-color-text']: barColor,
  } as Record<string, string>;

  return (
    <Card className={styles.relatedCard} size="small" variant="outlined">
      <Flex vertical gap="small" align="center" flex="1">
        <Image
          className={styles.advImage}
          height={64}
          preview={false}
          src={image}
          alt={imageAlt}
        />
        <Text font="body-overline" className={styles.statusText}>
          {name}
        </Text>
        <Tooltip title={tooltip}>
          <Space
            size={4}
            direction="vertical"
            className={styles.detailsSection}
          >
            {showProgress ? (
              <div className={styles.progressSection}>
                <Progress
                  style={barProps}
                  strokeLinecap="round"
                  showInfo={progress > 0}
                  className={styles.progressbar}
                  strokeColor={barColor}
                  percent={progress * 100}
                  size={['100%', 12]}
                />
              </div>
            ) : null}
            {label ? (
              <Text
                font="body-overline"
                className={styles.label}
                color={colorProp}
              >
                {label}
              </Text>
            ) : null}
            <Text font="body-overline">
              <StatusTag status={status} />
            </Text>
          </Space>
        </Tooltip>
      </Flex>
    </Card>
  );
}
export function AdvancementCard(props: AdvancementCardProps) {
  const status = advancementStatusToTagStatus(props.youthRecord?.status);
  const showProgress =
    !props.youthRecord ||
    status === StatusProp.STARTED ||
    status === StatusProp.NOT_STARTED;

  const auditFields = [
    'version',
    'dateCompleted',
    'leaderSignedDate',
    'dateStarted',
  ]
    .map(field => ({
      label: <>{startCase(field)}</>,
      id: field,
      value: props.youthRecord?.[field as keyof typeof props.youthRecord],
    }))
    .concat([
      {
        id: 'markedCompletedDate',
        label: (
          <FormattedMessage
            id="auditInfo.fieldOn"
            values={{
              field: startCase('markedCompleted'),
              date: props.youthRecord?.markedCompletedDate,
            }}
          />
        ),
        value: [
          props.youthRecord?.markedCompletedFirstName,
          props.youthRecord?.markedCompletedLastName,
        ]
          .filter(Boolean)
          .join(' '),
      },

      {
        id: 'leaderApprovedDate',
        label: (
          <FormattedMessage
            id="auditInfo.fieldOn"
            values={{
              field: startCase('leaderApproved'),
              date: props.youthRecord?.leaderApprovedDate,
            }}
          />
        ),
        value: [
          props.youthRecord?.leaderApprovedFirstName,
          props.youthRecord?.leaderApprovedLastName,
        ]
          .filter(Boolean)
          .join(' '),
      },

      {
        id: 'awardedDate',
        label: (
          <FormattedMessage
            id="auditInfo.fieldOn"
            values={{
              field: startCase('awarded'),
              date: props.youthRecord?.awardedDate,
            }}
          />
        ),
        value: [
          props.youthRecord?.awardedFirstName,
          props.youthRecord?.awardedLastName,
        ]
          .filter(Boolean)
          .join(' '),
      },
    ])
    .filter(item => !!item.value);
  const tooltip = props.youthRecord ? (
    <Space direction="vertical" size={0}>
      {auditFields.map(field => (
        <div key={field.id}>
          <Text font="body-caption" strong color="color-white">
            {field.label}:
          </Text>{' '}
          <Text font="body-caption" color="color-white">
            {field.value}
          </Text>
        </div>
      ))}
    </Space>
  ) : null;

  switch (props.type) {
    case 'award': {
      const { award } = props;
      const { youthRecord } = props;
      const status = advancementStatusToTagStatus(youthRecord?.status);
      return (
        <BaseCard
          name={award.name}
          tooltip={tooltip}
          image={award.imageUrl200}
          imageAlt="Award"
          progress={youthRecord?.percentCompleted || 0}
          showProgress={showProgress}
          status={status}
          label={
            showProgress ? null : (
              <div>
                {youthRecord?.awardedDate ||
                  youthRecord?.leaderApprovedDate ||
                  youthRecord?.dateEarned}
              </div>
            )
          }
        />
      );
    }
    case 'rank': {
      const { rank } = props;
      const { youthRecord } = props;
      const status = advancementStatusToTagStatus(youthRecord?.status);
      return (
        <BaseCard
          name={rank.name}
          tooltip={tooltip}
          image={rank.imageUrl200}
          imageAlt="Rank"
          progress={youthRecord?.percentCompleted || 0}
          showProgress={showProgress}
          status={status}
          label={
            showProgress ? null : (
              <div>
                {youthRecord?.awardedDate ||
                  youthRecord?.leaderApprovedDate ||
                  youthRecord?.dateEarned}
              </div>
            )
          }
        />
      );
    }

    case 'meritBadge': {
      const { meritBadge: mb, youthRecord } = props;

      return (
        <BaseCard
          name={mb.name}
          image={mb.imageUrl200}
          imageAlt="Merit Badge"
          progress={youthRecord?.percentCompleted || 0}
          tooltip={tooltip}
          label={
            showProgress ? null : (
              <div>
                {youthRecord?.awardedDate ||
                  youthRecord?.leaderApprovedDate ||
                  youthRecord?.dateCompleted ||
                  youthRecord?.dateStarted}
              </div>
            )
          }
          showProgress={showProgress}
          status={status}
        />
      );
    }
    default: {
      return null;
    }
  }
}

export default AdvancementCard;
